/* oxlint-disable @typescript-eslint/no-explicit-any */

import type { Either, LazyArg, Left, Right, ValueOrLazyArg } from '../types';
import { isInstanceOfError, tryCatchErrorSafe } from '../typescript/error';

/**
 * Constructs a new `Either` holding a `Left` value. This usually represents a failure, due to the right-bias of this
 * structure.
 *
 * @category constructors
 * @since 2.0.0
 */
export const left: <E = never, A = never>(e: E) => Either<E, A> = (e) => ({
	_tag: 'Left',
	left: e,
});

/**
 * Constructs a new `Either` holding a `Right` value. This usually represents a successful value due to the right bias
 * of this structure.
 *
 * @category constructors
 * @since 2.0.0
 */
export const right: <E = never, A = never>(a: A) => Either<E, A> = (a) => ({
	_tag: 'Right',
	right: a,
});

/**
 * Returns `true` if the either is an instance of `Left`, `false` otherwise.
 *
 * @category refinements
 * @since 2.0.0
 */
export const isLeft: <E>(ma: Either<E, unknown>) => ma is Left<E> = (ma) => ma?._tag === 'Left';

export const isLeftInstanceOf = <E, A, T extends E>(
	either: Either<E, A>,
	instance: new (...args: any[]) => T
): either is Left<T> => isLeft(either) && isInstanceOfError(either.left, instance);

/**
 * Returns `true` if the either is an instance of `Right`, `false` otherwise.
 *
 * @category refinements
 * @since 2.0.0
 */
export const isRight: <A>(ma: Either<unknown, A>) => ma is Right<A> = (ma) => ma?._tag === 'Right';

export const isLeftOrRight = <E, A>(either: unknown): either is Left<E> | Right<A> =>
	isLeft(either as Either<E, A>) || isRight(either as Either<E, A>);

export function getOrElse<E, A>(either: Either<E, A>, defaultValue: ValueOrLazyArg<A>): A {
	if (isRight(either)) {
		return either.right;
	}

	if (typeof defaultValue === 'function') {
		return (defaultValue as LazyArg<A>)();
	}

	return defaultValue;
}

/**
 * Unwraps a `Right`, throwing the `Left` otherwise — the equivalent of Rust's
 * `.unwrap()` / `.expect()`.
 *
 * For a boundary that cannot itself return an `Either`: a function whose
 * signature is fixed (`Promise<void>`, `Promise<T[]>`) but which consumes an
 * `Either`-returning helper. Keeping the helper's failures typed and letting the
 * boundary decide to throw is what puts the throw-or-degrade policy at the
 * boundary rather than baked into the helper.
 *
 * Prefer {@link getOrElse} wherever a default is meaningful. Reach for this only
 * when there is genuinely no recovery — a thrown error is strictly worse than a
 * handled one, so an `Either` that arrives here should be one whose `Left` means
 * "this operation cannot proceed", not "this operation found nothing".
 *
 * A non-`Error` `Left` is wrapped rather than thrown raw, so a `catch` can always
 * rely on receiving an `Error`.
 *
 * @example
 * const size = getOrThrow(stringifyJSON(value)); // throws the SyntaxError
 * const size = getOrThrow(parseJSON(raw), (e) => new ParseException(raw, e));
 */
export function getOrThrow<E, A>(either: Either<E, A>): A;
export function getOrThrow<E, A>(either: Either<E, A>, onLeft: (left: E) => unknown): A;
export function getOrThrow<E, A>(either: Either<E, A>, onLeft?: (left: E) => unknown): A {
	if (isRight(either)) {
		return either.right;
	}

	throw tryCatchErrorSafe(onLeft ? onLeft(either.left) : either.left);
}

export function getLeftOrElse<E, A, B>(
	either: Either<E, A>,
	orElse: (right: A) => Either<E, B> | B
): Either<E, B> {
	if (isLeft(either)) {
		return either;
	}

	const b = orElse(either.right);

	if (isLeftOrRight(b)) {
		return b;
	}

	return right(b);
}
